import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import { formatCurrency } from '../../utils/formatters';
import { API_BASE_URL } from '../../config';
import './embed.css';

const EmbedTradesTable = () => {
  const [searchParams] = useSearchParams();
  const bg = searchParams.get('bg') || '#0a0a0a';
  const limit = parseInt(searchParams.get('limit')) || 100;
  const refreshInterval = parseInt(searchParams.get('refresh')) || 5000;

  const [trades, setTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const socketRef = useRef(null);

  useEffect(() => {
    const fetchTrades = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/trades/recent?limit=${limit}`);
        if (res.ok) {
          const data = await res.json();
          const arr = Array.isArray(data) ? data : (data.trades || []);
          setTrades(arr.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, limit));
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };

    fetchTrades();

    socketRef.current = io(API_BASE_URL, { auth: { public: true } });
    socketRef.current.on('trade-executed', (data) => {
      setTrades(prev => {
        const trade = { ...data, timestamp: data.timestamp || new Date().toISOString(), isNew: true };
        const updated = [trade, ...prev].slice(0, limit);
        return updated;
      });
    });

    const interval = setInterval(fetchTrades, refreshInterval);
    return () => {
      if (socketRef.current) socketRef.current.disconnect();
      clearInterval(interval);
    };
  }, [limit, refreshInterval]);

  const formatTime = (ts) => {
    if (!ts) return '--';
    return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const getType = (trade) => {
    if (trade.tradeType === 'TRADE_CLOSE' || trade.type === 'CLOSE_LONG' || trade.type === 'CLOSE_SHORT') return 'CLOSE';
    return trade.type === 'LONG' || trade.type === 'BUY' ? 'LONG' : 'SHORT';
  };

  return (
    <div className="embed-table-page" style={{ background: bg === 'transparent' ? 'transparent' : bg }}>
      <div className="embed-table-header">
        <span className="embed-table-title">LIVE TRADES</span>
        <span className="embed-table-live-dot" />
        <span className="embed-table-count">{trades.length}</span>
        <button className="embed-fullscreen-btn" onClick={() => document.documentElement.requestFullscreen?.()}>
          &#x26F6;
        </button>
      </div>
      <div className="embed-table-wrap">
        <table className="embed-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Trader</th>
              <th>Stock</th>
              <th>Type</th>
              <th>Price</th>
              <th>Shares</th>
              <th>Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#666' }}>Loading...</td></tr>
            ) : trades.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#666' }}>No trades yet</td></tr>
            ) : (
              trades.map((trade, i) => {
                const type = getType(trade);
                const typeClass = type === 'CLOSE' ? 'close' : type === 'LONG' ? 'long' : 'short';
                return (
                  <tr key={trade.id || i} className={trade.isNew ? 'embed-row-new' : ''}>
                    <td className="embed-td-time">{formatTime(trade.timestamp)}</td>
                    <td className="embed-td-trader">{trade.username || trade.shortId || '???'}</td>
                    <td className="embed-td-stock">{trade.ticker || '???'}</td>
                    <td className={`embed-td-type ${typeClass}`}>{type}</td>
                    <td className="embed-td-price">{formatCurrency(trade.price)}</td>
                    <td className="embed-td-shares">{trade.shares || 1}</td>
                    <td className="embed-td-total">{formatCurrency((trade.price || 0) * (trade.shares || 1))}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default EmbedTradesTable;
